import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControlLabel,
    Switch,
    Alert,
    Box,
    CircularProgress,
    Typography,
    AppBar,
    Toolbar,
    IconButton,
    useTheme,
    useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth0 } from '@auth0/auth0-react';
import { createMissionTemplate, checkTemplateNameUnique } from '../network/apis/missionTemplate.api';
import { useOrganization } from '../context/OrganizationContext';

/**
 * Create Template Dialog Component
 * Allows creating an operation template from an existing operation
 *
 * Props:
 * @param {boolean} open - Dialog open state
 * @param {function} onClose - Close dialog callback
 * @param {Object} operation - Source operation data
 * @param {function} onSuccess - Success callback
 */
const CreateTemplateDialog = ({ open, onClose, operation, onSuccess }) => {
    const { getAccessTokenSilently } = useAuth0();
    const { currentOrganization, userRole } = useOrganization();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Normalize operation fields (handle both camelCase and snake_case)
    const normalizedOperation = operation ? {
        id: operation.id,
        operationId: operation.operationId || operation.operation_id,
        operationType: operation.operationType || operation.operation_type,
        maxAltitude: operation.maxAltitude || operation.max_altitude,
        radius: operation.radius,
        duration: operation.duration
    } : null;

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        isShared: false
    });

    // UI state
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [nameError, setNameError] = useState(null);
    const [checkingName, setCheckingName] = useState(false);

    // Check user role
    const isOwnerOrManager = userRole && ['owner', 'admin', 'manager'].includes(userRole.toLowerCase());

    // Reset form when dialog opens/closes
    useEffect(() => {
        if (open) {
            setFormData({
                name: '',
                description: '',
                isShared: false
            });
            setError(null);
            setNameError(null);
        }
    }, [open]);

    // Debounced name uniqueness check
    useEffect(() => {
        const checkName = async () => {
            if (!formData.name.trim()) {
                setNameError(null);
                return;
            }

            setCheckingName(true);
            try {
                const token = await getAccessTokenSilently();
                const isAvailable = await checkTemplateNameUnique(
                    formData.name,
                    token,
                    null,
                    currentOrganization?.organizationUuid || null
                );

                if (!isAvailable) {
                    setNameError('This template name is already in use. Please choose a different name.');
                } else {
                    setNameError(null);
                }
            } catch (err) {
            } finally {
                setCheckingName(false);
            }
        };

        const timer = setTimeout(checkName, 500);
        return () => clearTimeout(timer);
    }, [formData.name, currentOrganization]);

    const handleFormChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async () => {
        // Validation
        if (!formData.name.trim()) {
            setError('Template name is required');
            return;
        }

        if (nameError) {
            setError('Please fix the errors before submitting');
            return;
        }

        if (!operation) {
            setError('No operation data available');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const token = await getAccessTokenSilently();

            const templateData = {
                name: formData.name.trim(),
                description: formData.description.trim(),
                operationId: normalizedOperation.id,
                isShared: formData.isShared
            };

            await createMissionTemplate(
                templateData,
                token,
                currentOrganization?.organizationUuid || null
            );

            // Success
            if (onSuccess) {
                onSuccess();
            }
            onClose();
        } catch (err) {
            if (err.response?.data?.message) {
                setError(err.response.data.message);
            } else {
                setError('Failed to create template. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    if (!normalizedOperation) {
        return null;
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
            {isMobile && (
                <AppBar sx={{ position: 'relative' }}>
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={onClose}
                            disabled={loading}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                            Create Template
                        </Typography>
                        <Button
                            autoFocus
                            color="inherit"
                            onClick={handleSubmit}
                            disabled={loading || !formData.name.trim() || !!nameError || checkingName}
                        >
                            {loading ? 'Creating...' : 'Create'}
                        </Button>
                    </Toolbar>
                </AppBar>
            )}
            {!isMobile && (
                <DialogTitle>
                    Create Operation Template
                </DialogTitle>
            )}

            <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
                {/* Operation Info */}
                <Box sx={{ mb: isMobile ? 2 : 3, p: isMobile ? 1.5 : 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontSize: isMobile ? '0.85rem' : '0.875rem' }}>
                        Creating template from operation:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: isMobile ? 1 : 2, flexDirection: 'column', mt: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                            <strong>Operation ID:</strong> {normalizedOperation.operationId}
                        </Typography>
                        {normalizedOperation.duration && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                                <strong>Duration:</strong> {normalizedOperation.duration} minutes
                            </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                            The template will copy all flight parameters (altitude, radius, geometry, etc.) from this operation
                        </Typography>
                    </Box>
                </Box>

                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Form Fields */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1.5 : 2 }}>
                    <TextField
                        label="Template Name"
                        required
                        fullWidth
                        size={isMobile ? "small" : "medium"}
                        value={formData.name}
                        onChange={(e) => handleFormChange('name', e.target.value)}
                        placeholder="e.g., Downtown Building Inspection"
                        error={!!nameError}
                        helperText={nameError || 'Choose a unique, descriptive name for this template'}
                        InputProps={{
                            endAdornment: checkingName && <CircularProgress size={20} />
                        }}
                    />

                    <TextField
                        label="Description (Optional)"
                        fullWidth
                        multiline
                        rows={isMobile ? 2 : 3}
                        size={isMobile ? "small" : "medium"}
                        value={formData.description}
                        onChange={(e) => handleFormChange('description', e.target.value)}
                        placeholder="Describe when to use this template..."
                        helperText="Add details about the mission type, location, or special requirements"
                    />

                    {/* Sharing Option */}
                    <Box sx={{ p: isMobile ? 1.5 : 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isShared}
                                    onChange={(e) => handleFormChange('isShared', e.target.checked)}
                                    size={isMobile ? "small" : "medium"}
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ fontSize: isMobile ? '0.85rem' : '0.875rem' }}>
                                    Share with organization
                                </Typography>
                            }
                        />
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: isMobile ? 3.5 : 4, fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                            {formData.isShared
                                ? 'All team members can view and use this template'
                                : 'Only you can see and use this template'}
                        </Typography>

                        {isOwnerOrManager && (
                            <Alert severity="info" sx={{ mt: 1, fontSize: isMobile ? '0.8rem' : '0.875rem', py: isMobile ? 0.5 : 1 }}>
                                As {userRole}, you can always see all templates created by your team
                            </Alert>
                        )}
                    </Box>
                </Box>
            </DialogContent>

            {!isMobile && (
                <DialogActions>
                    <Button onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={loading || !formData.name.trim() || !!nameError || checkingName}
                        startIcon={loading && <CircularProgress size={16} />}
                    >
                        {loading ? 'Creating...' : 'Create Template'}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default CreateTemplateDialog;
